import { useMemo } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import styles from "./AuditLogModal.module.css";
import { useToast } from "../../../context/ToastContext";
import {
  useGetCompanySettingsAuditLogs,
  useExportCompanySettingsAuditLogsCsv,
} from "../../../api/sytem&settings/useCompanySettings";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
};

const AuditLogModal = ({ open, onClose }) => {
  const { showToast } = useToast();
  const {
    data: logs,
    loading,
    error,
  } = useGetCompanySettingsAuditLogs({ page: 1, limit: 50 }, open);
  const { execute: exportCsv, loading: exportLoading } =
    useExportCompanySettingsAuditLogsCsv();

  const formattedLogs = useMemo(
    () =>
      (logs || []).map((log) => ({
        id: log.id,
        date: formatDateTime(log.timestamp),
        user: log.user_name || "—",
        field: log.field_changed || "—",
        oldVal: log.old_value || "—",
        newVal: log.new_value || "—",
      })),
    [logs]
  );

  const handleExport = async () => {
    try {
      const response = await exportCsv();
      const blob = new Blob([response.data], { type: "text/csv" });
      const disposition = response.headers?.["content-disposition"] || "";
      const matchedName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      const filename = matchedName || "audit-logs.csv";

      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
      showToast("Audit log export started", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to export audit logs", "error");
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Audit Log — Company Settings"
      width="720px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={onClose}>
            Close
          </button>
          <button
            className={styles.exportBtn}
            onClick={handleExport}
            disabled={exportLoading}
          >
            {exportLoading ? "Exporting..." : "Export CSV"}
          </button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.note}>
          All entries are immutable and timestamped. Logs cannot be edited or
          deleted.
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>User</th>
                <th>Field Changed</th>
                <th>Old Value</th>
                <th>New Value</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className={styles.statusRow}>
                    Loading audit logs...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className={styles.statusRowError}>
                    Failed to load audit logs: {error}
                  </td>
                </tr>
              ) : formattedLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className={styles.statusRow}>
                    No audit log entries found.
                  </td>
                </tr>
              ) : (
                formattedLogs.map((log) => (
                  <tr key={log.id}>
                    <td className={styles.dateCell}>{log.date}</td>
                    <td className={styles.userCell}>{log.user}</td>
                    <td className={styles.fieldCell}>{log.field}</td>
                    <td className={styles.oldCell}>{log.oldVal}</td>
                    <td className={styles.newCell}>{log.newVal}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </BaseModal>
  );
};

export default AuditLogModal;